/** Display formatting for es-AR (CLAUDE.md § Communication conventions). */

import { formatCentsAr } from "@devengo/shared";

const money = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Exact for every amount in the exact range (formatCentsAr never divides in floating point).
 * Beyond it — a list total of huge vouchers, a number being typed — the screen must not crash
 * (Codex 009 round 4): it shows the approximate value. The API refuses to compute with those.
 */
export function formatCents(cents: number): string {
  if (!Number.isSafeInteger(cents))
    return Number.isFinite(cents) ? `≈ ${money.format(cents / 100)}` : "—";
  return formatCentsAr(cents);
}

/**
 * API decimals ("1234.56") for display. Text is formatted digit by digit, never through a double,
 * so a large amount keeps its last cent (Codex 015 M3 review); a number falls back to Intl.
 */
export function formatDecimal(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") {
    const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
    if (match) {
      const int = (match[2] ?? "0").replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const frac = (match[3] ?? "").padEnd(2, "0");
      const negative = match[1] && /[1-9]/.test(`${match[2]}${frac}`);
      return `${negative ? "-" : ""}${int},${frac}`;
    }
  }
  return money.format(Number(value));
}

/** The Buenos Aires calendar date of an instant, as dd/mm/yyyy. */
export function formatDateAr(iso: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(iso));
}

export function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Buenos_Aires",
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

export function formatCuit(value: string): string {
  const d = value.replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : value;
}

export function voucherNumber(pointOfSale: number, number: number): string {
  return `${String(pointOfSale).padStart(5, "0")}-${String(number).padStart(8, "0")}`;
}

export const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

export function periodName(year: number, month: number): string {
  return `${MONTHS[month - 1] ?? month} ${year}`;
}

/**
 * Reads what a person types in an es-AR amount field: "1.234,56", "1234,56", "1234".
 * Returns the API decimal ("1234.56") or null when it cannot be read with certainty.
 */
export function parseUserAmount(input: string): string | null {
  const text = input.trim().replace(/\s/g, "").replace(/^\$/, "");
  if (text === "") return "0";
  let normalized: string;
  if (text.includes(",")) {
    if (!/^\d{1,3}(\.\d{3})*,\d{1,2}$|^\d+,\d{1,2}$/.test(text)) return null;
    normalized = text.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(text)) {
    normalized = text.replace(/\./g, "");
  } else if (/^\d+(\.\d{1,2})?$/.test(text)) {
    normalized = text;
  } else {
    return null;
  }
  return normalized;
}

/** Integer cents from an API decimal, exactly (no float multiplication). */
export function decimalCents(value: string): number {
  const [int = "0", frac = ""] = value.split(".");
  return Number(int) * 100 + Number(frac.padEnd(2, "0").slice(0, 2));
}

export function centsDecimal(cents: number): string {
  return `${Math.trunc(cents / 100)}.${String(Math.abs(cents) % 100).padStart(2, "0")}`;
}

/** "1234.56" → "1.234,56" for an editable field. */
export function decimalToInput(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "" || Number(value) === 0) return "";
  return formatDecimal(value);
}

/**
 * An exchange rate as typed in es-AR: "1.234,50" or "1234,5" or "980". Up to six decimals after
 * the comma. A lone dot is a thousands separator ("1.234" = mil doscientos treinta y cuatro):
 * reading it as a decimal point would divide every peso amount by a thousand (Codex hito 3-5
 * finding 8). Returns the API decimal or null.
 */
export function parseUserRate(input: string): string | null {
  const text = input.trim().replace(/\s/g, "");
  if (text === "") return null;
  let normalized: string;
  if (text.includes(",")) {
    if (!/^\d{1,3}(\.\d{3})*,\d{1,6}$|^\d+,\d{1,6}$/.test(text)) return null;
    normalized = text.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(text) || /^\d+$/.test(text)) {
    normalized = text.replace(/\./g, "");
  } else {
    return null;
  }
  return Number(normalized) > 0 ? normalized : null;
}

/** Pesos from an amount in the voucher's currency, exactly as the Caja IVA and the books do. */
export { toPesosCents } from "@devengo/shared";
